// UI minimap — a tiny schematic of the screen that sits just above the HOME
// button. Each filled rectangle mirrors a real HUD panel's on-screen position;
// click it to show/hide that panel. The dimmer center block is a master toggle
// (show/hide everything). Hidden state persists in localStorage.
//
// The Fortune block (MAO COIN amount) is a THREE-click control: click 1 fades the panel's background away and leaves just the
// coin count; click 2 hides the count too; click 3 resets it (background and count back). The other blocks are simple on/off.
//
// Layout is a flexbox grid: four rows, each row a flex strip of cells. Uniform
// padding + gap (see hud.css) give every rectangle identical margins and edges,
// so the schematic stays crisp at any minimap size — no hand-tuned coordinates.

const KEY = 'mao_ui_hidden'

// Toggleable sections — `sel` is the real HUD element each rect mirrors.
const SECTIONS = [
  { key: 'tabs',   label: 'Top bar (kingdoms / profile)', sel: '.hud__kingdom-tabs-slot' },
  { key: 'title',  label: 'Title / logo',   sel: '.hud__title' },
  { key: 'wealth', label: 'Fortune',        sel: '.hud__wealth', tri: true },
  { key: 'stats',  label: 'Clicker count',  sel: '.hud__stats' },
  { key: 'rail',   label: 'Menu rail',      sel: '.hud__rail' },
  { key: 'goons',  label: 'Cat shop',       sel: '[data-goons]' },
  { key: 'log',    label: 'Message log',    sel: '.hud__log' },
]
const SECTION_BY_KEY = new Map(SECTIONS.map((s) => [s.key, s]))

// Visual rows (top → bottom). `grow` = the row's share of the box height; each
// cell's `flex` = its share of the row width. 'master' is the center filler.
const ROWS = [
  { grow: 1, cells: [{ key: 'tabs', flex: 1 }] },
  { grow: 2, cells: [{ key: 'title', flex: 1 }, { key: 'wealth', flex: 1.3 }, { key: 'stats', flex: 1 }] },
  { grow: 2, cells: [{ key: 'rail', flex: 1 }, { key: 'master', flex: 3.6 }, { key: 'goons', flex: 1 }] },
  { grow: 1, cells: [{ key: 'log', flex: 1 }] },
]

function loadHidden() {
  try { return new Set(JSON.parse(localStorage.getItem(KEY) || '[]')) } catch { return new Set() }
}
function saveHidden(set) {
  try { localStorage.setItem(KEY, JSON.stringify([...set])) } catch { /* private mode */ }
}

export function mountUiMinimap() {
  if (document.querySelector('.ui-minimap')) return // idempotent
  const hidden = loadHidden()

  const wrap = document.createElement('div')
  wrap.className = 'ui-minimap'

  let master = null
  for (const row of ROWS) {
    const rowEl = document.createElement('div')
    rowEl.className = 'ui-minimap__row'
    rowEl.style.flex = `${row.grow}`
    for (const cell of row.cells) {
      const btn = document.createElement('button')
      btn.style.flex = `${cell.flex}`
      if (cell.key === 'master') {
        btn.className = 'ui-minimap__master'
        master = btn
      } else {
        btn.className = 'ui-minimap__rect'
        btn.dataset.key = cell.key
        btn.title = `Show / hide: ${SECTION_BY_KEY.get(cell.key).label}`
      }
      rowEl.appendChild(btn)
    }
    wrap.appendChild(rowEl)
  }

  document.body.appendChild(wrap)

  // A three-step block (tri) keeps its middle step as `<key>Bg` in the saved set: 0 = normal, 1 = background gone (count stays),
  // 2 = hidden (the plain `<key>` entry, same as every other block, so "hide all" and old saves keep working; it keeps `<key>Bg`
  // too when it came from step 1, so the background stays off while the count fades out).
  const bgKey = (s) => `${s.key}Bg`
  const stateOf = (s) => (hidden.has(s.key) ? 2 : s.tri && hidden.has(bgKey(s)) ? 1 : 0)
  const TRI_TITLES = [
    'Fortune — click once to fade its background, leaving just the coin count',
    'Fortune — click again to hide the coin count too',
    'Fortune — click again to bring it all back',
  ]

  // Reflect a section's state onto the real element + its mini-rect.
  const apply = (s) => {
    const el = document.querySelector(s.sel)
    const st = stateOf(s)
    if (el) {
      el.classList.add('ui-toggleable') // opt the panel into the fade transition
      el.classList.toggle('ui-hidden', st === 2)
      // Step 2 keeps the background-off look while the count fades, so the plate never flashes back during the fade-out.
      el.classList.toggle('ui-bare', st === 1 || (st === 2 && !!s.tri && hidden.has(bgKey(s))))
    }
    const rect = wrap.querySelector(`[data-key="${s.key}"]`)
    if (rect) {
      rect.classList.toggle('off', st === 2)
      rect.classList.toggle('bare', st === 1)
      if (s.tri) rect.title = TRI_TITLES[st]
    }
  }
  const updateMaster = () => {
    const allHidden = SECTIONS.every((s) => hidden.has(s.key))
    master.classList.toggle('off', allHidden)
    master.title = allHidden ? 'Show all panels' : 'Hide all panels'
  }
  SECTIONS.forEach(apply)
  updateMaster()

  wrap.addEventListener('click', (e) => {
    // Master filler → show/hide everything at once.
    if (e.target.closest('.ui-minimap__master')) {
      const allHidden = SECTIONS.every((s) => hidden.has(s.key))
      if (allHidden) hidden.clear()
      else SECTIONS.forEach((s) => hidden.add(s.key))
      saveHidden(hidden)
      SECTIONS.forEach(apply)
      updateMaster()
      return
    }
    const rect = e.target.closest('.ui-minimap__rect')
    if (!rect) return
    const s = SECTION_BY_KEY.get(rect.dataset.key)
    if (!s) return
    if (s.tri) {
      const st = stateOf(s)
      if (st === 0) hidden.add(bgKey(s))                                   // 1: background fades, count stays
      else if (st === 1) hidden.add(s.key)                                  // 2: count hidden too (background stays off)
      else { hidden.delete(s.key); hidden.delete(bgKey(s)) }               // 3: reset
    } else if (hidden.has(s.key)) hidden.delete(s.key)
    else hidden.add(s.key)
    saveHidden(hidden)
    apply(s)
    updateMaster() // keep the master's lit/dim state in sync
  })
}
